import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import Icon from "../icon";
import UserContext from "../../context/user";
import CartContext from "../../context/cart";
import useCartBookingCreateMutation from "../../hooks/mutations/useCartBookingCreateMutation";
import useCartRequestUpdateMutation from "../../hooks/mutations/useCartRequestUpdateMutation";
import useBreakpoint from "../../hooks/useBreakpoint";
import SwiperArrow from "../swiper-arrow";
dayjs.extend(localeData);

const Arrow = ({ next = false, onClick, disabled }) => {
  return (
    <button
      className={classNames("pick-up-slots__arrow", next && "pick-up-slots__arrow--next")}
      onClick={onClick}
      disabled={disabled}
      aria-label="Arrow"
    >
      <Icon name="angle-down" />
    </button>
  );
};

const PickUpTimeSlots = ({ tempCartToSubmit, setTempBookingDate }) => {
  const { logged, callApi } = useContext(UserContext);
  const {
    labels,
    campaign: { bookingDeliveryEndDate },
    appLang: lang,
    survey,
    formData,
    partial,
    campaign,
    cartRequest,
    hideMyBooking,
    setHideMyBooking,
    handlePrevious,
    handleNext,
    setFormData,
  } = useContext(CartContext);
  const isMobile = useBreakpoint(575);
  const initRef = useRef();

  const pickUpPointId = useMemo(() => {
    const pickUpPointQuestionId = survey?.definition?.sections?.[4]?.questions?.find(
      (q) => q.questionType === "Number"
    )?.id;
    return formData?.[pickUpPointQuestionId];
  }, [formData, survey]);

  const reservationQuestionId = useMemo(
    () =>
      survey?.definition?.sections?.[4]?.questions?.find((q) => q.questionType === "Reservation")
        ?.id,
    [survey]
  );

  const prevRef = useRef();
  const nextRef = useRef();

  const today = useMemo(() => dayjs(), []);
  const months = useMemo(() => {
    dayjs.locale(lang?.value);
    return dayjs.months();
  }, [lang?.value]);
  const weekdays = useMemo(() => {
    dayjs.locale(lang?.value);
    return dayjs.weekdaysShort();
  }, [lang?.value]);

  const [selectedDate, setSelectedDate] = useState();
  const selectedDay = useMemo(() => selectedDate?.format("DD-MM-YYYY"), [selectedDate]);
  const [selectedTime, setSelectedTime] = useState();
  const [validationError, setValidationError] = useState();

  const {
    isFetching,
    error,
    data: availabilities,
  } = useQuery(
    ["availabilities", pickUpPointId],
    () =>
      callApi(
        `/cart/booking/${pickUpPointId}/pickupPointAvailability?${new URLSearchParams({
          dateFrom: today.add(3, "day").format("DD-MM-YYYY"),
          dateTo: dayjs(bookingDeliveryEndDate).add(1, "day").format("DD-MM-YYYY"),
        }).toString()}`
      ),
    {
      enabled: logged && !!pickUpPointId,
      select: (data) =>
        data
          ?.filter((item) => item.bookingAvailable)
          ?.map((date) => dayjs(Number(date.date)))
          ?.sort((a, b) => (a.isBefore(b) ? -1 : a.isAfter(b) ? 1 : 0)),
    }
  );

  const {
    isFetching: isSlotFetching,
    error: slotError,
    data: timeSlots,
    refetch: refetchTimeSlots,
  } = useQuery(
    ["slots", pickUpPointId, selectedDay],
    () =>
      callApi(
        `/cart/booking/${pickUpPointId}/pickupPointAvailability?${new URLSearchParams({
          dateFrom: selectedDay,
        }).toString()}`
      ),
    {
      enabled: logged && !!pickUpPointId && !!selectedDay && availabilities?.length > 0,
      select: (data) =>
        data
          ?.filter(
            (item) =>
              item.bookingAvailable ||
              (cartRequest?.reservationStart === item.date && !hideMyBooking)
          )
          ?.map((date) => dayjs(Number(date.date))),
    }
  );

  const pickUpPointQuestionId = useMemo(
    () =>
      survey?.definition?.sections?.[4]?.questions?.find((q) => q.questionType === "Number")?.id,
    [survey]
  );

  const [visibleDate, setVisibleDate] = useState();

  useEffect(() => {
    if (availabilities?.length > 0 && !initRef.current) {
      initRef.current = true;
      setVisibleDate(availabilities[0]);
    }
  }, [availabilities]);

  const selectedMonthLabel = useMemo(() => months[visibleDate?.month()], [visibleDate, months]);
  const selectedYearLabel = useMemo(() => visibleDate?.year(), [visibleDate]);
  const selectedMonthDays = useMemo(() => {
    const arr = Array.from({ length: visibleDate?.daysInMonth() }, (_, i) => i + 1)?.map((elem) => {
      const day = visibleDate.date(elem);
      return {
        id: elem,
        date: day,
        weekday: weekdays[day.day()],
        available: !!availabilities?.find((date) => date.isSame(day, "day")),
      };
    });
    return arr.filter((day) => (isMobile ? day.available : true));
  }, [visibleDate, weekdays, availabilities, isMobile]);

  const handlePrevMonth = () => setVisibleDate((current) => current.subtract(1, "M"));
  const handleNextMonth = () => setVisibleDate((current) => current.add(1, "M"));
  const handleDateSelection = (e) => {
    const selectedDay = e?.currentTarget?.id;
    if (selectedDay) {
      setSelectedDate(visibleDate.date(selectedDay));
      setSelectedTime();
    }
  };

  const handleTimeSlotSelection = (e) => {
    const selectedTimeSlot = e?.currentTarget?.id;
    if (selectedTimeSlot) {
      const [h, m] = selectedTimeSlot.split(":");
      setSelectedDate((current) => current.set("hour", h).set("minute", m));
      setSelectedTime(true);
    }
  };

  const initialSlide = useMemo(
    () => (visibleDate?.isSame(availabilities?.[0], "month") ? availabilities?.[0]?.date() - 1 : 0),
    [visibleDate, availabilities]
  );

  const {
    mutateAsync: createCartBooking,
    error: cartBookingError,
    reset: resetCartBookingMutation,
  } = useCartBookingCreateMutation();

  const { mutateAsync: updateCartRequest } = useCartRequestUpdateMutation();

  const updateCartRequestFromValues = useCallback(async () => {
    async function getReservationId() {
      const cartRequestReservationDate = dayjs(Number(cartRequest.reservationStart));
      const isSameAsSelected = cartRequestReservationDate.isSame(selectedDate, "minute");
      if (cartRequest?.reservationStart && isSameAsSelected) {
        return cartRequest?.reservationId;
      }
      return (await createCartBooking({ pickUpPointId, dateFrom: selectedDate })).reservationId;
    }

    let updateCartRequestPayload = {
      campaignId: campaign?.id,
      cartRequest,
      survey,
      formData,
      partial,
      email: cartRequest?.welfareEmail,
      phone: cartRequest?.welfarePhone,
    };

    if (tempCartToSubmit || cartRequest?.status === "BOOKED") {
      const newValues = {
        ...formData,
        ...(tempCartToSubmit?.values || {}),
      };

      if (!newValues[pickUpPointQuestionId]) {
        newValues[pickUpPointQuestionId] = Number(formData[pickUpPointQuestionId]);
      }

      setTempBookingDate((old) => ({ ...old, dateFrom: selectedDate }));
      handleNext();
      setFormData(newValues);
      return;
    }

    const reservationId = await getReservationId();

    if (reservationId) {
      updateCartRequestPayload.values = { [reservationQuestionId]: reservationId };
      await updateCartRequest(updateCartRequestPayload);
    }
    setHideMyBooking(false);
  }, [
    campaign?.id,
    cartRequest,
    survey,
    formData,
    partial,
    tempCartToSubmit,
    setHideMyBooking,
    selectedDate,
    createCartBooking,
    pickUpPointId,
    pickUpPointQuestionId,
    setTempBookingDate,
    handleNext,
    setFormData,
    reservationQuestionId,
    updateCartRequest,
  ]);

  const [checkSameDate, setCheckSameDate] = useState(true);

  const handleSubmit = useCallback(async () => {
    if (selectedDate && selectedTime) {
      updateCartRequestFromValues();
    } else {
      setValidationError(true);
    }
  }, [selectedDate, selectedTime, updateCartRequestFromValues]);

  useEffect(() => {
    (async () => {
      if (!checkSameDate) {
        return;
      }

      if (!availabilities?.length) {
        return;
      }

      if (!cartRequest?.reservationStart) {
        setCheckSameDate(false);
        return;
      }

      const reservation = dayjs(Number(cartRequest.reservationStart));
      if (!availabilities.some((a) => a.isSame(reservation, "day"))) {
        setCheckSameDate(false);
        return;
      }

      const hoursAvailabilities = await callApi(
        `/cart/booking/${pickUpPointId}/pickupPointAvailability?${new URLSearchParams({
          dateFrom: reservation.format("DD-MM-YYYY"),
        }).toString()}`
      );

      if (!hoursAvailabilities.some((item) => cartRequest?.reservationStart === item.date)) {
        setCheckSameDate(false);
        return;
      }

      setSelectedDate(reservation);
      setSelectedTime(true);
      setCheckSameDate(false);
    })();
  }, [availabilities, cartRequest, checkSameDate, callApi, pickUpPointId]);

  const handleRefreshSlots = () => {
    refetchTimeSlots();
    resetCartBookingMutation();
  };

  const prevMonthDisabled = useMemo(
    () => availabilities?.[0]?.isSame(visibleDate, "month"),
    [availabilities, visibleDate]
  );

  const nextMonthDisabled = useMemo(
    () => availabilities?.[availabilities.length - 1]?.isSame(visibleDate, "month"),
    [availabilities, visibleDate]
  );

  return (
    <>
      <h2 className="carrello-content__title">
        {labels?.WELFARE_SELECT_SLOT || "Select pick-up date and time slot"}
      </h2>
      {error ? (
        <div className="error">Error retrieving availabilities</div>
      ) : (
        <>
          <div className="pick-up-slots">
            {availabilities?.length === 0 ? (
              <div className="text-center">
                {labels?.WELFARE_CART_NO_AVAILABILITIES ||
                  "No availabilities for the selected pick-up point"}
              </div>
            ) : isFetching ? (
              <div className="loading-spinner" />
            ) : (
              <>
                <div className="pick-up-slots__month-wrapper">
                  <Arrow onClick={handlePrevMonth} disabled={prevMonthDisabled} />
                  <div className="pick-up-slots__month">
                    <span>{selectedMonthLabel}</span>
                    <span>{selectedYearLabel}</span>
                  </div>
                  <Arrow next={true} onClick={handleNextMonth} disabled={nextMonthDisabled} />
                </div>
                {visibleDate ? (
                  <div className="pick-up-slots__days-wrapper" key={visibleDate.toString()}>
                    <SwiperArrow swiper={true} prevRef={prevRef} />
                    <div className="pick-up-slots__days">
                      <Swiper
                        modules={[Navigation]}
                        slidesPerView="auto"
                        slidesPerGroup={7}
                        centerInsufficientSlides={true}
                        initialSlide={initialSlide}
                        loop={false}
                        navigation={{
                          prevEl: ".swiper-button--prev",
                          nextEl: ".swiper-button--next",
                        }}
                        spaceBetween={4}
                      >
                        {selectedMonthDays?.map((day, i) => (
                          <SwiperSlide key={day.id}>
                            <button
                              key={day.id}
                              className={classNames(
                                "pick-up-slots__day",
                                selectedDate &&
                                  day?.date.isSame(selectedDate, "day") &&
                                  "pick-up-slots__day--selected",
                                day?.date.isSame(today, "day") && "pick-up-slots__day--today"
                              )}
                              id={day.id}
                              onClick={handleDateSelection}
                              disabled={!day.available}
                            >
                              <span className="pick-up-slots__day-name" translate="no">
                                {day.weekday}
                              </span>
                              <span className="pick-up-slots__day-value">{day.date.date()}</span>
                            </button>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                    <SwiperArrow swiper={true} next={true} nextRef={nextRef} />
                  </div>
                ) : (
                  <div className="loading-spinner" />
                )}
              </>
            )}
            {selectedDate &&
              availabilities?.length > 0 &&
              (slotError ? (
                <div className="error">Error retrieving availabilities</div>
              ) : isSlotFetching ? (
                <div className="loading-spinner" />
              ) : (
                <div className="pick-up-slots__times-wrapper">
                  <h3 className="pick-up-slots__times-title">
                    {labels?.WELFARE_AVAILABLE_TIME_SLOTS || "Available slots"}
                  </h3>
                  <div className="pick-up-slots__times">
                    {timeSlots?.map((time, i) => (
                      <button
                        key={i}
                        id={time.format("HH:mm")}
                        className={classNames(
                          "pick-up-slots__time",
                          selectedDate.isSame(time, "minute") && "pick-up-slots__time--selected"
                        )}
                        onClick={handleTimeSlotSelection}
                      >
                        <span>{time.format("HH:mm")}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
          </div>
          <div className="form__buttons-wrapper">
            {validationError && (
              <div className="error text-center">
                {labels?.WELFARE_CART_MANDATORY_SLOTS_FIELD || "Error"}
              </div>
            )}
            {cartBookingError?.status === 403 && (
              <div className="pick-up-slots__booked-slot">
                <div className="error text-center">
                  {labels?.WELFARE_BOOKED_SLOT_TITLE || "Lo slot che hai richiesto non c'è più"}
                </div>
                <button className="link link--text" onClick={handleRefreshSlots}>
                  {labels?.WELFARE_CART_RELOAD_SLOTS || "Reload available slots"}
                </button>
              </div>
            )}
            <div className="form__buttons form__buttons--center">
              <button className="link" onClick={handlePrevious} type="button">
                <Icon name="angle-down" />
                {labels?.WELFARE_FORM_BTN_PREVIOUS || "Previous"}
              </button>
              <button className="button primary form__submit" onClick={handleSubmit}>
                {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PickUpTimeSlots;
